import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Layout and example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Data
import reportsBarChartData from "layouts/dashboard/data/reportsBarChartData";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";

import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";

const { sales, tasks } = reportsLineChartData;

function Dashboard() {
  const isNegativeValue = (value) => {
    if (value === null || value === undefined) return false;
    const numericValue =
      typeof value === "string" ? value.replace(/[^0-9.-]/g, "") : value;
    return Number(numericValue) < 0;
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [todayOrders, setTodayOrders] = useState({});
  const [drivers, setDrivers] = useState(0);
  const [customers, setCustomers] = useState(0);
  const [vehicles, setVehicles] = useState(0);
  const [charts, setCharts] = useState({
    ordersWeekly: reportsBarChartData,
    monthlyOrders: sales,
    monthlyIncome: tasks,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiRequest.get(endPoints.DASHBOARD);
      console.log("Dashboard response:", response);
      if (response.success) {
        setTodayOrders(response.data.todayOrders);
        setDrivers(response?.data?.driverStats);
        setCustomers(response?.data?.customerStats);
        setVehicles(response?.data?.vehicleStats);
        const incomingCharts = response?.data?.charts || {};
        setCharts((prev) => ({
          ordersWeekly: incomingCharts.ordersWeekly
            ? {
                labels: incomingCharts.ordersWeekly.labels || [],
                datasets: { label: "Orders", data: incomingCharts.ordersWeekly.data || [] },
              }
            : prev.ordersWeekly,
          monthlyOrders: incomingCharts.monthlyOrders
            ? {
                labels: incomingCharts.monthlyOrders.labels || [],
                datasets: { label: "Orders", data: incomingCharts.monthlyOrders.data || [] },
                summaryText: incomingCharts.monthlyOrders.summaryText,
              }
            : prev.monthlyOrders,
          monthlyIncome: incomingCharts.monthlyIncome
            ? {
                labels: incomingCharts.monthlyIncome.labels || [],
                datasets: { label: "Income", data: incomingCharts.monthlyIncome.data || [] },
                summaryText: incomingCharts.monthlyIncome.summaryText,
              }
            : prev.monthlyIncome,
        }));
      } else {
        setError(response.message || "Unexpected error");
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      setError("Failed to fetch dashboard. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="shopping_cart"
                title="Today's Orders"
                count={loading ? "Loading..." : todayOrders.today_orders ?? 0}
                percentage={{
                  color: isNegativeValue(todayOrders.percentage) ? "error" : "success",
                  amount: todayOrders.percentage ?? "0%",
                  label: `than yesterday ${todayOrders.yesterday_orders ?? 0} orders`,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon="people"
                title="Total Customers"
                count={loading ? "Loading..." : customers.total_customer ?? 0}
                percentage={{
                  color: isNegativeValue(customers.month_percentage) ? "error" : "success",
                  amount: customers.month_percentage ?? "0%",
                  label: `than last ${customers.last_month ?? 0} month`,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="info"
                icon="person"
                title="Total Drivers"
                count={loading ? "Loading..." : drivers.total_drivers ?? 0}
                percentage={{
                  color: isNegativeValue(drivers.month_percentage) ? "error" : "success",
                  amount: drivers.month_percentage ?? "0%",
                  label: `than last ${drivers.last_month ?? 0} month`,

                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="directions_car"
                title="Registered Vehicles"
                count={loading ? "Loading..." : vehicles?.total_vehicles ?? 0}
                percentage={{
                  color: isNegativeValue(vehicles?.month_percentage) ? "error" : "success",
                  amount: vehicles?.month_percentage ?? "0%",
                  label:  `than last ${vehicles?.last_month ?? 0} month`,
                }}
              />
            </MDBox>
          </Grid>
        </Grid>

        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="info"
                  title="Orders"
                  description="Current Week Orders"
                  chart={charts.ordersWeekly}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="success"
                  title="Monthly Orders"
                  description={charts.monthlyOrders?.summaryText || "Monthly orders overview"}
                  chart={charts.monthlyOrders}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="dark"
                  title="Monthly Income"
                  description={charts.monthlyIncome?.summaryText || "Monthly income overview"}
                  chart={charts.monthlyIncome}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>

      <Footer />

      {/* Loader */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Snackbar */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={3000}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => {
            setError(null);
            setSuccess(null);
          }}
          severity={error ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Dashboard;
